import { Direction } from "@microsoft/fast-web-utilities";
import { accentPalette as defaultAccentPalette, neutralPalette as defaultNeutralPalette, } from "./default-palette";
/**
 * The default values for {@link FASTDesignSystem}
 * @public
 */
export const fastDesignSystemDefaults = {
    typeRampMinus2FontSize: "10px",
    typeRampMinus2LineHeight: "16px",
    typeRampMinus1FontSize: "12px",
    typeRampMinus1LineHeight: "16px",
    typeRampBaseFontSize: "14px",
    typeRampBaseLineHeight: "20px",
    typeRampPlus1FontSize: "16px",
    typeRampPlus1LineHeight: "24px",
    typeRampPlus2FontSize: "20px",
    typeRampPlus2LineHeight: "28px",
    typeRampPlus3FontSize: "28px",
    typeRampPlus3LineHeight: "36px",
    typeRampPlus4FontSize: "34px",
    typeRampPlus4LineHeight: "44px",
    typeRampPlus5FontSize: "46px",
    typeRampPlus5LineHeight: "56px",
    typeRampPlus6FontSize: "60px",
    typeRampPlus6LineHeight: "72px",
    accentBaseColor: "#DA1A5F",
    accentPalette: defaultAccentPalette,
    backgroundColor: "#181818",
    baseHeightMultiplier: 10,
    baseHorizontalSpacingMultiplier: 3,
    cornerRadius: 3,
    density: 0,
    designUnit: 4,
    direction: Direction.ltr,
    disabledOpacity: 0.3,
    focusOutlineWidth: 2,
    neutralPalette: defaultNeutralPalette,
    outlineWidth: 1,
    /**
     * Recipe Deltas
     */
    accentFillRestDelta: 0,
    accentFillHoverDelta: 4,
    accentFillActiveDelta: -5,
    accentFillFocusDelta: 0,
    accentFillSelectedDelta: 12,
    accentForegroundRestDelta: 0,
    accentForegroundHoverDelta: 6,
    accentForegroundActiveDelta: -4,
    accentForegroundFocusDelta: 0,
    neutralFillRestDelta: 7,
    neutralFillHoverDelta: 10,
    neutralFillActiveDelta: 5,
    neutralFillFocusDelta: 0,
    neutralFillSelectedDelta: 7,
    neutralFillInputRestDelta: 0,
    neutralFillInputHoverDelta: 0,
    neutralFillInputActiveDelta: 0,
    neutralFillInputFocusDelta: 0,
    neutralFillInputSelectedDelta: 0,
    neutralFillStealthRestDelta: 0,
    neutralFillStealthHoverDelta: 5,
    neutralFillStealthActiveDelta: 3,
    neutralFillStealthFocusDelta: 0,
    neutralFillStealthSelectedDelta: 7,
    neutralFillToggleHoverDelta: 8,
    neutralFillToggleActiveDelta: -5,
    neutralFillToggleFocusDelta: 0,
    baseLayerLuminance: -1,
    neutralFillCardDelta: 3,
    neutralForegroundHoverDelta: 0,
    neutralForegroundActiveDelta: 0,
    neutralForegroundFocusDelta: 0,
    neutralDividerRestDelta: 8,
    neutralOutlineRestDelta: 25,
    neutralOutlineHoverDelta: 40,
    neutralOutlineActiveDelta: 16,
    neutralOutlineFocusDelta: 25,
    neutralContrastFillRestDelta: 0,
    neutralContrastFillHoverDelta: -3,
    neutralContrastFillActiveDelta: 7,
    neutralContrastFillFocusDelta: 0,
};
/**
 * Returns the argument if basic, otherwise calls the DesignSystemResolver function.
 *
 * @param arg A value or a DesignSystemResolver function
 * @param designSystem The design system config.
 */
export function evaluateDesignSystemResolver(arg, designSystem) {
    return typeof arg === "function"
        ? arg(designSystem)
        : arg;
}
/**
 * Safely retrieves the value from a key of the DesignSystem.
 */
export function getDesignSystemValue(key) {
    return (designSystem) => {
        return designSystem && designSystem[key] !== undefined
            ? designSystem[key]
            : fastDesignSystemDefaults[key];
    };
}
/**
 * Retrieve the backgroundColor when invoked with a DesignSystem
 */
export const backgroundColor = getDesignSystemValue("backgroundColor");
/**
 * Retrieve the accentBaseColor when invoked with a DesignSystem
 */
export const accentBaseColor = getDesignSystemValue("accentBaseColor");
/**
 * Retrieve the cornerRadius when invoked with a DesignSystem
 */
export const cornerRadius = getDesignSystemValue("cornerRadius");
/**
 * Retrieve the neutral palette from the design system
 */
export const neutralPalette = getDesignSystemValue("neutralPalette");
/**
 * Retrieve the accent palette from the design system
 */
export const accentPalette = getDesignSystemValue("accentPalette");
/**
 * Retrieve the designUnit from the design system
 */
export const designUnit = getDesignSystemValue("designUnit");
/**
 * Retrieve the baseHeightMultiplier from the design system
 */
export const baseHeightMultiplier = getDesignSystemValue("baseHeightMultiplier");
/**
 * Retrieve the baseHorizontalSpacingMultiplier from the design system
 */
export const baseHorizontalSpacingMultiplier = getDesignSystemValue("baseHorizontalSpacingMultiplier");
/**
 * Retrieve the outlineWidth from the design system
 */
export const outlineWidth = getDesignSystemValue("outlineWidth");
/**
 * Retrieve the focusOutlineWidth from the design system
 */
export const focusOutlineWidth = getDesignSystemValue("focusOutlineWidth");
/**
 * Retrieve the disabledOpacity from the design system
 */
export const disabledOpacity = getDesignSystemValue("disabledOpacity");
/**
 * Retrieve the disabledOpacity from the design system
 */
export const direction = getDesignSystemValue("direction");
export const accentFillRestDelta = getDesignSystemValue("accentFillRestDelta");
export const accentFillHoverDelta = getDesignSystemValue("accentFillHoverDelta");
export const accentFillActiveDelta = getDesignSystemValue("accentFillActiveDelta");
export const accentFillFocusDelta = getDesignSystemValue("accentFillFocusDelta");
export const accentFillSelectedDelta = getDesignSystemValue("accentFillSelectedDelta");
export const accentForegroundRestDelta = getDesignSystemValue("accentForegroundRestDelta");
export const accentForegroundHoverDelta = getDesignSystemValue("accentForegroundHoverDelta");
export const accentForegroundActiveDelta = getDesignSystemValue("accentForegroundActiveDelta");
export const accentForegroundFocusDelta = getDesignSystemValue("accentForegroundFocusDelta");
export const neutralFillRestDelta = getDesignSystemValue("neutralFillRestDelta");
export const neutralFillHoverDelta = getDesignSystemValue("neutralFillHoverDelta");
export const neutralFillActiveDelta = getDesignSystemValue("neutralFillActiveDelta");
export const neutralFillFocusDelta = getDesignSystemValue("neutralFillFocusDelta");
export const neutralFillSelectedDelta = getDesignSystemValue("neutralFillSelectedDelta");
export const neutralFillInputRestDelta = getDesignSystemValue("neutralFillInputRestDelta");
export const neutralFillInputHoverDelta = getDesignSystemValue("neutralFillInputHoverDelta");
export const neutralFillInputActiveDelta = getDesignSystemValue("neutralFillInputActiveDelta");
export const neutralFillInputFocusDelta = getDesignSystemValue("neutralFillInputFocusDelta");
export const neutralFillInputSelectedDelta = getDesignSystemValue("neutralFillInputSelectedDelta");
export const neutralFillStealthRestDelta = getDesignSystemValue("neutralFillStealthRestDelta");
export const neutralFillStealthHoverDelta = getDesignSystemValue("neutralFillStealthHoverDelta");
export const neutralFillStealthActiveDelta = getDesignSystemValue("neutralFillStealthActiveDelta");
export const neutralFillStealthFocusDelta = getDesignSystemValue("neutralFillStealthFocusDelta");
export const neutralFillStealthSelectedDelta = getDesignSystemValue("neutralFillStealthSelectedDelta");
export const neutralFillToggleHoverDelta = getDesignSystemValue("neutralFillToggleHoverDelta");
export const neutralFillToggleActiveDelta = getDesignSystemValue("neutralFillToggleActiveDelta");
export const neutralFillToggleFocusDelta = getDesignSystemValue("neutralFillToggleFocusDelta");
export const baseLayerLuminance = getDesignSystemValue("baseLayerLuminance");
export const neutralFillCardDelta = getDesignSystemValue("neutralFillCardDelta");
export const neutralForegroundHoverDelta = getDesignSystemValue("neutralForegroundHoverDelta");
export const neutralForegroundActiveDelta = getDesignSystemValue("neutralForegroundActiveDelta");
export const neutralForegroundFocusDelta = getDesignSystemValue("neutralForegroundFocusDelta");
export const neutralDividerRestDelta = getDesignSystemValue("neutralDividerRestDelta");
export const neutralOutlineRestDelta = getDesignSystemValue("neutralOutlineRestDelta");
export const neutralOutlineHoverDelta = getDesignSystemValue("neutralOutlineHoverDelta");
export const neutralOutlineActiveDelta = getDesignSystemValue("neutralOutlineActiveDelta");
export const neutralOutlineFocusDelta = getDesignSystemValue("neutralOutlineFocusDelta");
export const neutralContrastFillRestDelta = getDesignSystemValue("neutralContrastFillRestDelta");
export const neutralContrastFillHoverDelta = getDesignSystemValue("neutralContrastFillHoverDelta");
export const neutralContrastFillActiveDelta = getDesignSystemValue("neutralContrastFillActiveDelta");
export const neutralContrastFillFocusDelta = getDesignSystemValue("neutralContrastFillFocusDelta");
